/**
 * Production-Ready Error Types
 * 
 * Comprehensive error handling with proper status codes and logging
 */

export enum ErrorCode {
  // Authentication Errors (400-403)
  INVALID_CREDENTIALS = 'AUTH_001',
  ACCOUNT_LOCKED = 'AUTH_002',
  SESSION_EXPIRED = 'AUTH_003',
  INSUFFICIENT_PERMISSIONS = 'AUTH_004',
  
  // Validation Errors (400)
  INVALID_EMAIL = 'VAL_001',
  INVALID_PASSWORD = 'VAL_002',
  MISSING_REQUIRED_FIELD = 'VAL_003',
  INVALID_FORMAT = 'VAL_004',
  
  // Business Logic Errors (422)
  DUPLICATE_EMAIL = 'BIZ_001',
  ORGANIZATION_NOT_FOUND = 'BIZ_002',
  PERSON_NOT_FOUND = 'BIZ_003',
  TEAM_FULL = 'BIZ_004',
  CANNOT_REMOVE_LEADER = 'BIZ_005',
  
  // System Errors (500)
  DATABASE_ERROR = 'SYS_001',
  CONNECTION_ERROR = 'SYS_002',
  CONFIGURATION_ERROR = 'SYS_003',
  UNKNOWN_ERROR = 'SYS_999'
}

export abstract class BaseError extends Error {
  public readonly code: ErrorCode;
  public readonly statusCode: number;
  public readonly isOperational: boolean;
  public readonly context?: any;

  constructor(
    message: string,
    code: ErrorCode,
    statusCode: number,
    isOperational: boolean = true,
    context?: any
  ) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    this.context = context;
    
    Error.captureStackTrace(this, this.constructor);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      statusCode: this.statusCode,
      context: this.context
    };
  }
}

// Authentication Errors
export class InvalidCredentialsError extends BaseError {
  constructor(context?: any) {
    super(
      'Invalid email or password',
      ErrorCode.INVALID_CREDENTIALS,
      401,
      true,
      context
    );
  }
}

export class AccountLockedError extends BaseError {
  constructor(remainingTime?: number) {
    super(
      'Account is locked due to too many failed attempts',
      ErrorCode.ACCOUNT_LOCKED,
      403,
      true,
      { remainingTime }
    );
  }
}

export class SessionExpiredError extends BaseError {
  constructor() {
    super(
      'Session has expired',
      ErrorCode.SESSION_EXPIRED,
      401,
      true
    );
  }
}

// Validation Errors
export class ValidationError extends BaseError {
  constructor(message: string, field?: string) {
    super(
      message,
      ErrorCode.INVALID_FORMAT,
      400,
      true,
      { field }
    );
  }
}

export class DuplicateEmailError extends BaseError {
  constructor(email: string) {
    super(
      `Email ${email} is already registered`,
      ErrorCode.DUPLICATE_EMAIL,
      422,
      true,
      { email }
    );
  }
}

// Not Found Errors
export class PersonNotFoundError extends BaseError {
  constructor(id: string) {
    super(
      `Person with id ${id} not found`,
      ErrorCode.PERSON_NOT_FOUND,
      404,
      true,
      { personId: id }
    );
  }
}

export class OrganizationNotFoundError extends BaseError {
  constructor(id: string) {
    super(
      `Organization with id ${id} not found`,
      ErrorCode.ORGANIZATION_NOT_FOUND,
      404,
      true,
      { organizationId: id }
    );
  }
}

// System Errors
export class DatabaseError extends BaseError {
  constructor(operation: string, originalError?: Error) {
    super(
      `Database operation failed: ${operation}`,
      ErrorCode.DATABASE_ERROR,
      500,
      false,
      { operation, originalError: originalError?.message }
    );
  }
}

export class ConnectionError extends BaseError {
  constructor(service: string, originalError?: Error) {
    super(
      `Failed to connect to ${service}`,
      ErrorCode.CONNECTION_ERROR,
      500,
      false,
      { service, originalError: originalError?.message }
    );
  }
}

/**
 * Error handler utility
 */
export class ErrorHandler {
  static isOperationalError(error: Error): boolean {
    if (error instanceof BaseError) {
      return error.isOperational;
    }
    return false;
  }

  static getHttpStatusCode(error: Error): number {
    if (error instanceof BaseError) {
      return error.statusCode;
    }
    return 500;
  }

  static formatErrorResponse(error: Error): any {
    if (error instanceof BaseError) {
      return {
        error: {
          code: error.code,
          message: error.message,
          ...(process.env.NODE_ENV === 'development' && { 
            stack: error.stack,
            context: error.context 
          })
        }
      };
    }

    return {
      error: {
        code: ErrorCode.UNKNOWN_ERROR,
        message: 'An unexpected error occurred',
        ...(process.env.NODE_ENV === 'development' && { 
          stack: error.stack 
        })
      }
    };
  }
}